import { ColDef, GetRowIdParams } from 'ag-grid-community';
import { filter, switchMap, tap } from 'rxjs';

import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ApmTranslatePipe, ToastService } from '@mitsubishi/core';
import { ExpiringMarkup, PriceListService } from '@mitsubishi/pricing-list/data-access';
import { ApmFaIconComponent, ButtonComponent } from '@mitsubishi/shared/ui';
import { DialogService } from '@mitsubishi/ui/dialog';
import {
  TableActionClickEvent,
  TableComponent,
  TableGetCustomIconColumnDef,
  TableIconActions,
} from '@mitsubishi/ui/table';

import { EXPIRING_MARKUP_RESOURCE_IDS } from '../../expiring-markup.constants';
import { getBlockingExpiringMarkups } from '../../expiring-markup.utils';
import { getPriceListGroupAutoColumnDef, getPriceListGroupColumns } from '../../price-list-subfamilies-columns';

export interface ExpiringMarkupBlockingDialogData {
  rows: ExpiringMarkup[];
}

export const EXPIRING_MARKUP_GO_TO_WPL = 'goToWpl';

/** `true` once no blocking draft is left; `EXPIRING_MARKUP_GO_TO_WPL` when the user postpones. */
export type ExpiringMarkupBlockingDialogResult = true | typeof EXPIRING_MARKUP_GO_TO_WPL;

@Component({
  selector: 'apm-expiring-markup-blocking-dialog',
  templateUrl: './expiring-markup-blocking-dialog.component.html',
  styleUrl: './expiring-markup-blocking-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ApmTranslatePipe, ButtonComponent, TableComponent, ApmFaIconComponent],
})
export class ExpiringMarkupBlockingDialogComponent {
  readonly #priceListService = inject(PriceListService);
  readonly #dialogService = inject(DialogService);
  readonly #dialogRef =
    inject<MatDialogRef<ExpiringMarkupBlockingDialogComponent, ExpiringMarkupBlockingDialogResult>>(MatDialogRef);
  readonly #toastService = inject(ToastService);

  protected readonly resourceIds = EXPIRING_MARKUP_RESOURCE_IDS;
  protected readonly rows = signal(inject<ExpiringMarkupBlockingDialogData>(MAT_DIALOG_DATA).rows);
  protected readonly count = computed(() => this.rows().length);

  protected readonly autoGroupColumnDef = getPriceListGroupAutoColumnDef();
  protected readonly columnDefs: ColDef[] = [
    ...getPriceListGroupColumns(),
    {
      ...TableGetCustomIconColumnDef({
        field: 'markupId',
        icon: 'a_delete',
        action: TableIconActions.Delete,
        tooltip: '446107',
        isHidden: data => !data,
      }),
      pinned: 'right',
    },
  ];

  protected readonly getRowId = (params: GetRowIdParams) => (params.data as ExpiringMarkup).markupId;

  protected deleteAll(): void {
    const ids = this.#allIds();
    this.#dialogService
      .openConfirmationDialog({
        titleResource: { resourceId: this.resourceIds.confirmDeleteAllTitle, resourceParams: [ids.length] },
      })
      .pipe(
        filter(Boolean),
        switchMap(() => this.#priceListService.bulkDeleteMarkup(ids)),
        switchMap(() => this.#priceListService.getDraftMarkups())
      )
      .subscribe(list => this.#applyRefresh(list));
  }

  protected publishAll(): void {
    this.#priceListService
      .bulkFinalizeMarkup(this.#allIds())
      .pipe(
        tap(() => this.#toastService.success({ resourceId: this.resourceIds.publishSuccess })),
        switchMap(() => this.#priceListService.getDraftMarkups())
      )
      .subscribe(list => this.#applyRefresh(list));
  }

  protected goToWpl(): void {
    this.#dialogRef.close(EXPIRING_MARKUP_GO_TO_WPL);
  }

  protected onTableAction(event: TableActionClickEvent): void {
    if (event.action === TableIconActions.Delete) {
      this.#deleteOne((event.data as ExpiringMarkup).markupId);
    }
  }

  #deleteOne(markupId: string): void {
    this.#priceListService
      .bulkDeleteMarkup([markupId])
      .pipe(switchMap(() => this.#priceListService.getDraftMarkups()))
      .subscribe(list => this.#applyRefresh(list));
  }

  #allIds(): string[] {
    return this.rows().map(row => row.markupId);
  }

  #applyRefresh(list: ExpiringMarkup[]): void {
    const blocking = getBlockingExpiringMarkups(list);
    if (!blocking.length) {
      this.#dialogRef.close(true);
      return;
    }
    this.rows.set(blocking);
  }
}
